<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { closeArr, online, searchIcon } from '@/assets/svg/common.svg';
import CatalogueCollectionItem from './CatalogueCollectionItem.vue';

const { t } = useI18n({ useScope: 'global' });

const collectionSearch = ref('');

const MODULE_OPTIONS = [
  {
    id: 'click_and_collect',
    icon: 'pi-shopping-cart',
    labelKey: 'commerceDetail.modulesCnc',
  },
  {
    id: 'pos_register',
    icon: 'pi-calculator',
    labelKey: 'commerceDetail.modulesCaisse',
  },
  {
    id: 'pds_display',
    icon: 'pi-desktop',
    labelKey: 'commerceDetail.modulesPds',
  },
  { id: 'kiosk', icon: 'pi-tablet', labelKey: 'commerceDetail.modulesKiosk' },
] as const;
function toggleModule(list: string[], id: string) {
  const idx = list.indexOf(id);
  const next = [...list];
  if (idx >= 0) next.splice(idx, 1);
  else next.push(id);
  return next;
}

const props = defineProps<{
  selectedModule: string | null;
  showCollections: boolean;
  visibleCollections: Array<{
    id: string | null;
    name: string;
    products: any[];
  }>;
  draggingProduct: any;
  dragOverCollection: string | null;
  isFiltering: boolean;
  editingSection: string | null;
  editCatLang: 'fr' | 'nl' | 'en';
  editCatDesigFr: string;
  editCatDesigNl: string;
  editCatDesigEn: string;
  editCatDescFr: string;
  editCatDescNl: string;
  editCatDescEn: string;
  editCatModules: string[];
  isSavingCatEdit: boolean;
  isAutoTranslatingCatDesig: boolean;
  isAutoTranslatingCatDesc: boolean;
  movingCategoryId: string | null;
  deletingProductId: string | null;
  showAddInput: boolean;
  newColFr: string;
  newColNl: string;
  newColEn: string;
  newColLang: 'fr' | 'nl' | 'en';
  newColModules: string[];
  isAutoTranslatingNewCol: boolean;
  isAddingCollection: boolean;
  expandedSections: string[];
  fullWidth?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'create-collection'): void;
  (e: 'toggle-section', key: string): void;
  (e: 'start-edit', colId: string | null, colName: string): void;
  (e: 'cancel-edit'): void;
  (e: 'save-edit', colId: string | null, colName: string): void;
  (e: 'delete-collection', colId: string | null, colName: string): void;
  (e: 'move-up', index: number): void;
  (e: 'move-down', index: number): void;
  (e: 'remove-product', productId: string, colId: string | null): void;
  (e: 'dragover', key: string): void;
  (e: 'dragleave'): void;
  (e: 'drop', colId: string | null, colName: string): void;
  (e: 'update:editCatLang', val: 'fr' | 'nl' | 'en'): void;
  (e: 'update:editCatDesigFr', val: string): void;
  (e: 'update:editCatDesigNl', val: string): void;
  (e: 'update:editCatDesigEn', val: string): void;
  (e: 'update:editCatDescFr', val: string): void;
  (e: 'update:editCatDescNl', val: string): void;
  (e: 'update:editCatDescEn', val: string): void;
  (e: 'update:editCatModules', val: string[]): void;
  (e: 'cat-desig-input', lang: 'fr' | 'nl' | 'en', val: string): void;
  (e: 'cat-desc-input', lang: 'fr' | 'nl' | 'en', val: string): void;
  (e: 'show-add-input'): void;
  (e: 'cancel-add'): void;
  (e: 'confirm-add'): void;
  (e: 'update:newColFr', val: string): void;
  (e: 'update:newColNl', val: string): void;
  (e: 'update:newColEn', val: string): void;
  (e: 'update:newColLang', val: 'fr' | 'nl' | 'en'): void;
  (e: 'update:newColModules', val: string[]): void;
  (e: 'new-col-input', lang: 'fr' | 'nl' | 'en', val: string): void;
  (e: 'visit'): void;
}>();

const isExpanded = (col: { id: string | null; name: string }) =>
  props.expandedSections.includes(col.id ?? col.name);

const filteredCollections = computed(() => {
  const q = collectionSearch.value.trim().toLowerCase();
  if (!q) return props.visibleCollections;
  return props.visibleCollections.filter(
    (col) =>
      col.name.toLowerCase().includes(q) ||
      col.products.some((p) => p.designation?.toLowerCase().includes(q)),
  );
});
</script>

<template>
  <div
    v-if="fullWidth || selectedModule"
    class="flex h-full flex-col gap-4 overflow-hidden"
    :class="
      fullWidth ? 'w-full' : 'w-[300px] shrink-0 border-l border-gray-100'
    "
  >
    <!-- Header -->
    <div class="flex flex-wrap items-center gap-2">
      <div
        @click="emit('close')"
        class="flex items-center justify-center"
        v-html="closeArr"
      />
      <div
        class="onlignebg flex h-10 flex-1 flex-row items-center justify-center gap-2 px-1"
      >
        <div v-html="online" />
        <p class="text-14px font-tt-medium-Normal text-[#2D2317]">
          {{ t('catalogue.online') }}
        </p>
      </div>
      <button
        @click="emit('visit')"
        class="btnbgValue h-10 flex-1 items-center justify-center text-white"
      >
        {{ t('catalogue.visit') }}
      </button>
    </div>

    <!-- No collections empty state -->
    <div
      v-if="!showCollections"
      class="flex flex-1 flex-col items-center justify-center gap-3 py-8"
    >
      <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
        <rect x="20" y="15" width="68" height="85" rx="8" fill="#E8F4FD" />
        <rect
          x="20"
          y="15"
          width="68"
          height="85"
          rx="8"
          stroke="#B8D9F0"
          stroke-width="2"
        />
        <rect x="32" y="30" width="44" height="5" rx="2.5" fill="#B8D9F0" />
        <rect x="32" y="42" width="36" height="5" rx="2.5" fill="#B8D9F0" />
        <rect x="32" y="54" width="40" height="5" rx="2.5" fill="#B8D9F0" />
        <rect x="32" y="66" width="28" height="5" rx="2.5" fill="#B8D9F0" />
        <circle cx="82" cy="82" r="22" fill="#FFF3E0" />
        <circle cx="82" cy="82" r="22" stroke="#F68402" stroke-width="2" />
        <text
          x="82"
          y="89"
          text-anchor="middle"
          font-size="22"
          font-weight="bold"
          fill="#F68402"
        >
          ?
        </text>
      </svg>
      <p class="text-grand-text text-16px font-tt-semiBold-Normal">
        Aucune collection
      </p>
      <p class="text-secondary-text text-13px px-4 text-center leading-snug">
        Vous n'avez encore créé aucune collection
      </p>
      <button
        @click="emit('create-collection')"
        class="bg-primary-900 text-14px font-tt-medium-Normal hover:bg-primary-700 mt-2 rounded-xl px-5 py-2.5 text-white transition-colors"
      >
        Créer une collection
      </button>
    </div>

    <!-- Collections list -->
    <div v-else class="flex max-h-[90vh] flex-1 flex-col">
      <!-- Recherche locale collections -->
      <div class="relative shrink-0 px-3 pb-2">
        <span
          v-html="searchIcon"
          class="pointer-events-none absolute top-1/2 left-6 -translate-y-1/2"
        />
        <input
          v-model="collectionSearch"
          type="text"
          :placeholder="t('catalogue.searchPlaceholder')"
          class="text-14px focus:border-primary-900 text-grand-text placeholder:text-secondary-text w-full rounded-xl border border-gray-200 bg-white py-2 pr-3 pl-9 outline-none"
        />
      </div>
      <p
        v-if="draggingProduct"
        class="text-primary-900 font-tt-medium-Normal mb-2 shrink-0 animate-pulse text-center text-[10px]"
      >
        Déposez sur une collection ↓
      </p>
      <div
        class="no-scrollbar min-h-0 flex-1 overflow-y-auto transition-colors"
      >
        <CatalogueCollectionItem
          v-for="(col, colIndex) in filteredCollections"
          :key="col.id ?? col.name"
          :col="col"
          :col-index="colIndex"
          :is-expanded="isExpanded(col)"
          :is-editing="editingSection === (col.id ?? col.name)"
          :drag-over-key="dragOverCollection"
          :is-dragging-product="!!draggingProduct"
          :edit-cat-lang="editCatLang"
          :edit-cat-desig-fr="editCatDesigFr"
          :edit-cat-desig-nl="editCatDesigNl"
          :edit-cat-desig-en="editCatDesigEn"
          :edit-cat-desc-fr="editCatDescFr"
          :edit-cat-desc-nl="editCatDescNl"
          :edit-cat-desc-en="editCatDescEn"
          :edit-cat-modules="editCatModules"
          :is-saving-cat-edit="isSavingCatEdit"
          :is-auto-translating-cat-desig="isAutoTranslatingCatDesig"
          :is-auto-translating-cat-desc="isAutoTranslatingCatDesc"
          :total-count="visibleCollections.length"
          :moving-category-id="movingCategoryId"
          :deleting-product-id="deletingProductId"
          @toggle="emit('toggle-section', col.id ?? col.name)"
          @start-edit="emit('start-edit', col.id, col.name)"
          @cancel-edit="emit('cancel-edit')"
          @save-edit="emit('save-edit', col.id, col.name)"
          @delete="emit('delete-collection', col.id, col.name)"
          @move-up="emit('move-up', colIndex)"
          @move-down="emit('move-down', colIndex)"
          @remove-product="(pId) => emit('remove-product', pId, col.id)"
          @dragover="emit('dragover', col.id ?? col.name)"
          @dragleave="emit('dragleave')"
          @drop="emit('drop', col.id, col.name)"
          @update:editCatLang="(v) => emit('update:editCatLang', v)"
          @update:editCatDesigFr="(v) => emit('update:editCatDesigFr', v)"
          @update:editCatDesigNl="(v) => emit('update:editCatDesigNl', v)"
          @update:editCatDesigEn="(v) => emit('update:editCatDesigEn', v)"
          @update:editCatDescFr="(v) => emit('update:editCatDescFr', v)"
          @update:editCatDescNl="(v) => emit('update:editCatDescNl', v)"
          @update:editCatDescEn="(v) => emit('update:editCatDescEn', v)"
          @update:editCatModules="(v) => emit('update:editCatModules', v)"
          @cat-desig-input="(lang, val) => emit('cat-desig-input', lang, val)"
          @cat-desc-input="(lang, val) => emit('cat-desc-input', lang, val)"
        />

        <div
          v-if="filteredCollections.length === 0"
          class="flex flex-col items-center justify-center gap-1 px-4 py-6"
        >
          <p class="text-secondary-text text-12px text-center">
            Aucune collection ne contient de produit correspondant à cette
            recherche.
          </p>
        </div>
      </div>

      <!-- Add collection input -->
      <div
        v-if="showAddInput"
        class="flex shrink-0 flex-col gap-2.5 border-t border-gray-100 bg-gray-50 px-3 py-3"
      >
        <div class="flex items-center justify-between">
          <p class="text-12px font-tt-medium-Normal text-grand-text">
            {{ t('catalogue.collections.new') }}
          </p>
          <select
            :value="newColLang"
            @change="
              emit(
                'update:newColLang',
                ($event.target as HTMLSelectElement).value as
                  'fr' | 'nl' | 'en',
              )
            "
            class="text-11px text-secondary-text cursor-pointer rounded-lg border border-gray-200 bg-white px-1.5 py-0.5 outline-none"
          >
            <option value="fr">FR</option>
            <option value="nl">NL</option>
            <option value="en">EN</option>
          </select>
        </div>
        <div
          v-for="lang in ['fr', 'nl', 'en'] as const"
          :key="lang"
          class="flex items-center gap-1.5"
        >
          <span
            class="text-11px font-tt-medium-Normal w-5 shrink-0"
            :class="
              newColLang === lang ? 'text-primary-900' : 'text-secondary-text'
            "
            >{{ lang.toUpperCase() }}</span
          >
          <div
            v-if="newColLang !== lang && isAutoTranslatingNewCol"
            class="border-t-primary-900 h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-gray-200"
          />
          <input
            :value="
              lang === 'fr' ? newColFr : lang === 'nl' ? newColNl : newColEn
            "
            @input="
              emit(
                'new-col-input',
                lang,
                ($event.target as HTMLInputElement).value,
              );
              lang === 'fr'
                ? emit(
                    'update:newColFr',
                    ($event.target as HTMLInputElement).value,
                  )
                : lang === 'nl'
                  ? emit(
                      'update:newColNl',
                      ($event.target as HTMLInputElement).value,
                    )
                  : emit(
                      'update:newColEn',
                      ($event.target as HTMLInputElement).value,
                    );
            "
            @keyup.enter="emit('confirm-add')"
            @keyup.escape="emit('cancel-add')"
            type="text"
            :placeholder="`Nom ${lang.toUpperCase()}`"
            :autofocus="lang === 'fr'"
            class="text-13px text-grand-text flex-1 rounded-lg border px-2 py-1.5 outline-none"
            :style="{
              borderColor: newColLang === lang ? '#F89303' : '#e5e7eb',
            }"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <p
            class="text-11px font-tt-medium-Normal text-secondary-text tracking-wide uppercase"
          >
            {{ t('allCollections.modulesLabel') }}
          </p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="m in MODULE_OPTIONS"
              :key="m.id"
              type="button"
              @click="
                emit(
                  'update:newColModules',
                  toggleModule([...newColModules], m.id),
                )
              "
              class="text-11px font-tt-medium-Normal flex items-center gap-1 rounded-lg border px-2 py-1"
              :class="
                newColModules.includes(m.id)
                  ? 'border-primary-900 bg-primary-900/10 text-grand-text'
                  : 'text-secondary-text border-gray-200'
              "
            >
              <i :class="['pi', m.icon]" style="font-size: 0.6875rem" />
              {{ t(m.labelKey) }}
            </button>
          </div>
        </div>

        <div class="flex gap-2 pt-0.5">
          <button
            @click="emit('cancel-add')"
            class="text-12px text-grand-text font-tt-medium-Normal h-8 flex-1 rounded-lg border border-gray-200 hover:bg-gray-100"
          >
            {{ t('catalogue.collections.cancel') }}
          </button>
          <button
            @click="emit('confirm-add')"
            :disabled="isAddingCollection || !newColFr.trim()"
            class="text-12px font-tt-medium-Normal h-8 flex-1 rounded-lg text-white disabled:opacity-50"
            style="background: linear-gradient(135deg, #333 0%, #111 100%)"
          >
            {{ isAddingCollection ? '...' : t('catalogue.edit.add') }}
          </button>
        </div>
      </div>

      <!-- Add collection button -->
      <button
        @click="emit('show-add-input')"
        class="text-primary-900 text-14px font-tt-medium-Normal hover:bg-fond-claire flex w-full shrink-0 items-center gap-1.5 border-t border-gray-100 px-4 py-3"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="#F68402" stroke-width="1.4" />
          <path
            d="M7 4v6M4 7h6"
            stroke="#F68402"
            stroke-width="1.4"
            stroke-linecap="round"
          />
        </svg>
        {{ t('catalogue.collections.add') }}
      </button>
    </div>
  </div>
</template>
